import apiClient from './apiClient';
import type {
  CodigoEnviado,
  DetalheDoDocumento,
  ListaDeDocumentos,
  ResumoPublico,
  StatusDoDocumento,
  Verificacao,
} from '../types/assinatura';

/**
 * As rotas de assinatura respondem o corpo direto, sem o envelope `{ data }`. O `apiClient` tipa
 * o corpo como talvez ausente; aqui ele falha alto em vez de espalhar `?.` pelas telas.
 */
function corpo<T>(resposta: unknown): T {
  if (resposta === undefined || resposta === null) throw new Error('O servidor respondeu sem conteúdo.');
  return resposta as T;
}

/** O endereço absoluto da API, para o que o navegador abre sozinho (PDF numa aba nova). */
function urlDaApi(caminho: string): string {
  const base = apiClient.getClient().defaults.baseURL ?? '/api/v1';
  return `${base.replace(/\/$/, '')}${caminho}`;
}

function salvarBlob(blob: Blob, nome: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

class AssinaturaService {
  private base = '/assinaturas';
  private publica = '/assinatura-publica';

  // ── Interno ──

  async listar(filtro: { status?: StatusDoDocumento; pagina: number; limite: number }): Promise<ListaDeDocumentos> {
    return corpo(
      await apiClient.get(this.base, {
        params: { status: filtro.status, page: filtro.pagina, limit: filtro.limite },
      })
    );
  }

  async detalhar(id: number): Promise<DetalheDoDocumento> {
    return corpo(await apiClient.get(`${this.base}/${id}`));
  }

  /** O download interno vai com o token de sessão, por isso passa pelo cliente e não por link. */
  async baixar(id: number, qual: 'original' | 'assinado', nome: string): Promise<void> {
    const resposta = await apiClient.getClient().get<Blob>(`${this.base}/${id}/arquivo/${qual}`, { responseType: 'blob' });
    salvarBlob(resposta.data, nome);
  }

  async reenviar(id: number): Promise<void> {
    await apiClient.post(`${this.base}/${id}/reenviar`);
  }

  async cancelar(id: number, motivo: string): Promise<void> {
    await apiClient.post(`${this.base}/${id}/cancelar`, { motivo });
  }

  // ── Público (o link é a credencial) ──

  async resumo(token: string): Promise<ResumoPublico> {
    return corpo(await apiClient.get(`${this.publica}/link/${encodeURIComponent(token)}`));
  }

  urlDoDocumento(token: string): string {
    return urlDaApi(`${this.publica}/link/${encodeURIComponent(token)}/documento`);
  }

  urlDoAssinado(token: string): string {
    return urlDaApi(`${this.publica}/link/${encodeURIComponent(token)}/assinado`);
  }

  async pedirCodigo(token: string, cpf: string): Promise<CodigoEnviado> {
    return corpo(await apiClient.post(`${this.publica}/link/${encodeURIComponent(token)}/codigo`, { cpf }));
  }

  async assinar(token: string, entrada: { codigo: string; imagem: string; aceite: boolean }): Promise<ResumoPublico> {
    return corpo(await apiClient.post(`${this.publica}/link/${encodeURIComponent(token)}/assinar`, entrada));
  }

  async recusar(token: string, motivo: string): Promise<void> {
    await apiClient.post(`${this.publica}/link/${encodeURIComponent(token)}/recusar`, { motivo });
  }

  async verificar(codigo: string): Promise<Verificacao> {
    return corpo(await apiClient.get(`${this.publica}/verificar/${encodeURIComponent(codigo)}`));
  }
}

export const assinaturaService = new AssinaturaService();
export default assinaturaService;
